import { useConnection } from "@solana/wallet-adapter-react";
import type { TulipStrategyVaultPosition } from "@stepfinance/vaults";
import { TulipStrategyVaultPositionFetcher } from "@stepfinance/vaults";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useMarkets } from "../../contexts/market";
import { useRefreshContext } from "../../contexts/refresh";
import { getTokenByMint } from "../../utils/tokenInfo";
import { useVaultsQuery } from "../queries/useVaultsQuery";

export const useTulipStrategyVaults = (): TulipStrategyVaultPosition[] => {
  const { connection } = useConnection();
  const { publicKey } = useEnrichedWallet();
  const { marketsInitialised, priceInUSD } = useMarkets();
  const { refreshID } = useRefreshContext();

  const {
    data: { tulipStrategyVaults },
  } = useVaultsQuery();

  const fetcher = useMemo(() => {
    if (!publicKey || tulipStrategyVaults.vaultData.length === 0) {
      return undefined;
    }
    return new TulipStrategyVaultPositionFetcher(publicKey, connection, tulipStrategyVaults);
  }, [connection, publicKey, tulipStrategyVaults]);

  const { data: positions, refetch } = useQuery(
    ["tulip", "strategy", publicKey?.toBase58()],
    async () => {
      if (!fetcher || !publicKey || !marketsInitialised) {
        return null;
      }
      return await fetcher.fetchPositions(priceInUSD, getTokenByMint);
    },
    {
      enabled: !!fetcher && !!publicKey && marketsInitialised,
    },
  );

  useEffect(() => {
    void refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshID]);

  return positions ?? [];
};
